import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTableCellsTdTitleAttribute } from '@mylighthouse/prism-ember';
import PrismTable from '@mylighthouse/prism-ember/components/prism/table';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs from 'dayjs';
import MarketSnapshotsTableTdWithVariance, {
  type MarketSnapshotsTableTdWithVarianceSignature,
} from 'lumen/components/market/snapshots/table/td-with-variance';
import type { MarketPerformanceDataItem } from 'lumen/models';
import type {
  Kpi,
  MarketPerformanceMetrics,
  MarketPerformanceTotals,
  MetricType,
} from 'lumen/models/market-performance/data-item';
import { KPIS } from 'lumen/models/market-performance/data-item';
import {
  GROUP_BY,
  GROUP_COLUMNS_BY,
  type GroupBy,
  type GroupColumnsBy,
  KPI_TRANSLATIONS,
  METRIC_TYPE_TRANSLATIONS,
} from 'lumen/utils/constants/market-snapshots-constants';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';

import styles from './table.module.css';

export interface MarketSnapshotsTableSignature {
  Args: {
    dataItems: MarketPerformanceDataItem[];
    groupBy?: GroupBy;
    groupColumnsBy: GroupColumnsBy;
    isLoading?: boolean;
    onSort: (orderBy: string) => void;
    orderBy: string;
    totals?: MarketPerformanceTotals;
    varianceType: Variance;
    visibleKpis: readonly Kpi[];
    visibleMetricTypes: MetricType[];
  };
}

type MetricDisplayValue = Record<
  Kpi,
  MarketSnapshotsTableTdWithVarianceSignature['Args']['cellData']
>;

function buildMetricDisplayValue(
  totals: MarketPerformanceMetrics,
  metricType: MetricType,
  varianceType: Variance,
): MetricDisplayValue {
  const varianceKey = varianceType === 'absolute' ? 'absolute' : 'percent';
  const resolvedVarianceType =
    metricType === 'otb' ? VARIANCES.NONE : varianceType;

  return {
    adr: {
      value: totals.adr,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.adr[varianceKey],
    },
    occupancy: {
      isValuePercent: true,
      value: totals.occupancy,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.occupancy[varianceKey],
    },
    revenue: {
      value: totals.revenue,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.revenue[varianceKey],
    },
    revpar: {
      value: totals.revpar,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.revpar[varianceKey],
    },
    rooms: {
      value: totals.rooms,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.rooms[varianceKey],
    },
  };
}

export default class MarketSnapshotsTable extends Component<MarketSnapshotsTableSignature> {
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];

  get columns(): CustomEmberTableColumn[] {
    const { groupColumnsBy, visibleKpis, visibleMetricTypes } = this.args;

    const nameColumn: CustomEmberTableColumn = {
      hasRightBorder: true,
      isFixed: 'left',
      isSortable: !this.isMonthView,
      minWidth: 200,
      name: this.intl.t(this.isMonthView ? 'general.month' : 'general.name'),
      tdComponent: PrismTableCellsTdTitleAttribute,
      valuePath: 'name',
      width: 200,
    };

    if (groupColumnsBy === GROUP_COLUMNS_BY.KPIS) {
      return [
        nameColumn,
        ...visibleKpis.map(
          (kpi): CustomEmberTableColumn => ({
            name: this.intl.t(KPI_TRANSLATIONS[kpi]),
            subcolumns: visibleMetricTypes.map((metricType, index) =>
              this.buildCellColumn({
                hasRightBorder: index === visibleMetricTypes.length - 1,
                kpi,
                metricType,
                name: this.intl.t(METRIC_TYPE_TRANSLATIONS[metricType]),
              }),
            ),
            valuePath: '',
          }),
        ),
      ];
    }

    return [
      nameColumn,
      ...visibleMetricTypes.map(
        (metricType): CustomEmberTableColumn => ({
          name: this.intl.t(METRIC_TYPE_TRANSLATIONS[metricType]),
          subcolumns: KPIS.map((kpi, index) =>
            this.buildCellColumn({
              hasRightBorder: index === KPIS.length - 1,
              kpi,
              metricType,
              name: this.intl.t(KPI_TRANSLATIONS[kpi]),
            }),
          ),
          valuePath: '',
        }),
      ),
    ];
  }

  get footerRows(): CustomEmberTableRow[] {
    const { totals, visibleMetricTypes } = this.args;

    if (!totals) {
      return [];
    }

    const metricTypeToDisplayValues = visibleMetricTypes.reduce<
      Partial<Record<MetricType, MetricDisplayValue>>
    >((acc, metricType) => {
      const item = totals[metricType];
      if (!item?.totals) {
        return acc;
      }

      acc[metricType] = buildMetricDisplayValue(
        item.totals,
        metricType,
        this.args.varianceType,
      );

      return acc;
    }, {});

    return [
      {
        displayValue: metricTypeToDisplayValues,
        name: this.intl.t('general.total'),
      },
    ];
  }

  get isMonthView(): boolean {
    return this.args.groupBy === GROUP_BY.MONTH;
  }

  get rows(): CustomEmberTableRow[] {
    const groups = new Map<
      string,
      {
        metricTypeToDisplayValues: Partial<
          Record<MetricType, MetricDisplayValue>
        >;
        name: string;
      }
    >();

    for (const item of this.args.dataItems) {
      const { date, subscription } = item;
      const key = (this.isMonthView ? date : subscription?.id) ?? '';

      if (this.isMonthView && !key) {
        continue;
      }

      const group = groups.get(key) ?? {
        metricTypeToDisplayValues: {},
        name: this.isMonthView
          ? dayjs
              .utc(date)
              .format(this.dates.partialDateFormat.YEAR_AND_MONTH_LONG)
          : (subscription?.hotelName ?? ''),
      };

      // The API sends `totals: null` for metrics without data (e.g. no user
      // forecast); the row still renders, with an empty cell.
      if (item.totals) {
        group.metricTypeToDisplayValues[item.metricType] =
          buildMetricDisplayValue(
            item.totals,
            item.metricType,
            this.args.varianceType,
          );
      }

      groups.set(key, group);
    }

    return [...groups.values()].map(({ metricTypeToDisplayValues, name }) => ({
      displayValue: metricTypeToDisplayValues,
      name,
    }));
  }

  get sorts(): { isAscending: boolean; valuePath: string }[] {
    const { orderBy } = this.args;
    const isDescending = orderBy.startsWith('-');

    // Month rows come back in date order from the API and can't be sorted.
    if (this.isMonthView) {
      return [];
    }

    return [
      {
        valuePath: isDescending ? orderBy.slice(1) : orderBy,
        isAscending: !isDescending,
      },
    ];
  }

  buildCellColumn({
    hasRightBorder,
    kpi,
    metricType,
    name,
  }: {
    hasRightBorder: boolean;
    kpi: Kpi;
    metricType: MetricType;
    name: string;
  }): CustomEmberTableColumn {
    return {
      displayValuePath: `displayValue.${metricType}.${kpi}`,
      hasRightBorder,
      isSortable: !this.isMonthView,
      name,
      tdComponent: MarketSnapshotsTableTdWithVariance,
      textAlign: 'right',
      valuePath: `${metricType}.totals.${kpi}`,
    };
  }

  @action
  onUpdateSorts(sorts: { isAscending: boolean; valuePath: string }[]): void {
    const sort = sorts[0];

    if (!sort) {
      // Empty array = ember-table's "unsorted" state. Toggle direction instead.
      const { orderBy } = this.args;
      const isDescending = orderBy.startsWith('-');

      this.args.onSort(isDescending ? orderBy.slice(1) : `-${orderBy}`);

      return;
    }

    const orderBy = sort.isAscending ? sort.valuePath : `-${sort.valuePath}`;

    this.args.onSort(orderBy);
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @footerRows={{this.footerRows}}
      @isLoading={{@isLoading}}
      @onUpdateSorts={{this.onUpdateSorts}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      @sorts={{this.sorts}}
      class={{styles.table}}
      data-test-table
    />
  </template>
}
